import React, { FC, useCallback, useEffect, useState } from 'react';

import { differenceInDays } from 'date-fns';
import delv from 'dlv';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import ActionButton from 'components/action-button';
import Modal from 'components/modal';
import Picture from 'components/picture';
import SanityBlockRenderer from 'components/sanity-block-renderer';
import { TextExpander } from 'components/text-expander';
import { CartOfferState } from 'generated/rbi-graphql';
import { useOffersContext } from 'state/offers';
import {
  OfferActivationStatus,
  activationStatusFromOfferState,
  getEndingDate,
  timeLeftToRedeem,
} from 'utils/offers';
import { toast } from 'utils/toast';

const StyledModal = styled(Modal)`
  && {
    border-radius: ${Styles.borderRadius};
    background: ${Styles.color.white};
  }
`;

const StyledPicture = styled(Picture)`
  overflow: visible;
  position: relative;
  width: 100%;
  height: auto;
`;

const Content = styled.div`
  align-items: flex-start;
  display: flex;
  flex-flow: column nowrap;
  padding: 1rem;
  width: 100%;
`;

const Heading = styled.div`
  color: ${Styles.color.primary};
  font-size: 2rem;
  font-weight: 900;
  line-height: 0.95;
  margin: 0;
`;

const Description = styled.p`
  color: ${Styles.color.black};
  font-size: 1.2rem;
  font-weight: ${Styles.fontWeight.heavy};
  line-height: 1.1;
  margin: 0.5rem 0;
`;

const Expiry = styled.p`
  color: ${Styles.color.black};
  font-size: 1rem;
  line-height: 1.1;
  margin-block-start: 0.5em;
  margin-block-end: 1em;
`;

const Error = styled.p`
  color: ${Styles.color.error};
  font-size: 0.75rem;
  align-self: center;
  margin: 0.5rem 0;
`;

const StyledSanityBlockRenderer = styled(SanityBlockRenderer)`
  & * {
    font-size: 1rem;
  }
`;

interface IOfferRedemptionModalProps {
  modalIsOpen: boolean;
  closeModal(): void;
}

const OfferRedemptionModal: FC<IOfferRedemptionModalProps> = ({ closeModal, modalIsOpen }) => {
  const { selectedOffer: offer, offersFeedback, activateOffer } = useOffersContext();
  // Regular offer
  const feedbackForRegularOffer = offer && offersFeedback?.[offer.tokenId || offer._id];
  // Locked Offer
  const feedbackForLockedOffer =
    offer &&
    offersFeedback &&
    Object.values(offersFeedback).find(
      feedback => feedback.tokenId === offer.tokenId || feedback.couponId === offer._id
    );
  const feedbackForOffer = feedbackForRegularOffer || feedbackForLockedOffer;
  const { formatMessage, formatRelativeTime } = useIntl();

  const [offerActivationStatus, setOfferActivationStatus] = useState(
    activationStatusFromOfferState(
      feedbackForOffer ? feedbackForOffer.offerState : CartOfferState.CONSUMED
    )
  );

  useEffect(() => {
    setOfferActivationStatus(
      activationStatusFromOfferState(
        feedbackForOffer ? feedbackForOffer.offerState : CartOfferState.CONSUMED
      )
    );
  }, [feedbackForOffer, offer]);

  const handleActivateOffer = useCallback(() => {
    if (!offer || !feedbackForOffer || !feedbackForOffer.tokenId) {
      return;
    }

    setOfferActivationStatus(OfferActivationStatus.IN_PROGRESS);
    activateOffer(feedbackForOffer.tokenId)
      .then(({ isOfferActivated }) => {
        /**
         * this will be false if the offer wasn't activated for some reason.
         * It's all we get back from our API at this time.
         */
        if (isOfferActivated) {
          // Success condition
          setOfferActivationStatus(OfferActivationStatus.ACTIVATED);
          toast.success(formatMessage({ id: 'offerActivationSuccess' }));
          return closeModal();
        }

        // Successful submission, but activation failed.
        setOfferActivationStatus(OfferActivationStatus.FAILED);
      })
      .catch(() => {
        setOfferActivationStatus(OfferActivationStatus.FAILED);
        toast.error(formatMessage({ id: 'offerActivationError' }));
      });
  }, [offer, feedbackForOffer, activateOffer, formatMessage, closeModal]);

  if (!modalIsOpen || !offer) {
    return null;
  }

  const offerFeedback = offersFeedback?.[offer.tokenId || offer._id];
  const endingDate = getEndingDate(timeLeftToRedeem(offerFeedback));
  const heading = delv(offer, 'name.localeRaw.0.children.0.text', '');
  const description = delv(offer, 'description.localeRaw.0.children.0.text', '');
  const terms = delv(offer, 'moreInfo.localeRaw', '');
  const howToRedeemSanityContent = delv(offer, 'howToRedeem.localeRaw', '');

  const alt =
    delv(offer, 'localizedImage.locale.imageDescription') ||
    delv(offer, 'name.localeRaw.0.children.0.text');

  const buttonText = (status: OfferActivationStatus) => {
    switch (status) {
      case OfferActivationStatus.ACTIVATED:
        return formatMessage({ id: 'activated' });
      case OfferActivationStatus.FAILED:
        return formatMessage({ id: 'retry' });
      default:
        return formatMessage({ id: 'activate' });
    }
  };

  return (
    <StyledModal onDismiss={closeModal}>
      <StyledPicture
        placeholderAspectRatio={1.5}
        objectFitContain={false}
        image={offer?.localizedImage?.locale?.app!}
        alt={alt || formatMessage({ id: 'offerPicture' })}
      />
      <Content data-testid="redemption-modal">
        <Heading id="modal-heading">{heading}</Heading>
        <Description>{description}</Description>
        {endingDate && (
          <Expiry>
            {formatMessage(
              { id: 'expiresIn' },
              {
                expiryDays: formatRelativeTime(differenceInDays(endingDate, Date.now()), 'day', {
                  numeric: 'auto',
                }),
              }
            )}
          </Expiry>
        )}
        {/* This ActionButton needs to be wrapped by a div to prevent ios overflow issue */}
        <div>
          <ActionButton
            onClick={handleActivateOffer}
            isLoading={offerActivationStatus === OfferActivationStatus.IN_PROGRESS}
            disabled={offerActivationStatus === OfferActivationStatus.ACTIVATED}
            data-testid="dialog-confirm-btn"
            skipLogEvent
          >
            {buttonText(offerActivationStatus)}
          </ActionButton>
        </div>
        {offerActivationStatus === OfferActivationStatus.FAILED && (
          <Error>{formatMessage({ id: 'activationFailed' })}</Error>
        )}
        {terms && (
          <TextExpander
            closedText={formatMessage({ id: 'offerTerms' })}
            openText={formatMessage({ id: 'offerTerms' })}
          >
            <StyledSanityBlockRenderer content={terms} />
          </TextExpander>
        )}
        {howToRedeemSanityContent && (
          <TextExpander
            closedText={formatMessage({ id: 'howToRedeem' })}
            openText={formatMessage({ id: 'howToRedeem' })}
            defaultExpanded
          >
            <StyledSanityBlockRenderer content={howToRedeemSanityContent} />
          </TextExpander>
        )}
      </Content>
    </StyledModal>
  );
};

export default OfferRedemptionModal;
